import React from "react";
import { ArrowRight, Anchor, Wrench, Ship, Waves } from "lucide-react";
import PlaceholderNote from "@/components/content/PlaceholderNote";
import Seo from "@/components/Seo";
import { LLink } from "@/i18n/LLink";
import { useTranslation } from "react-i18next";
import { useLang } from "@/i18n/LangContext";
import { pageContent, pickLang } from "@/i18n/pageContent";

const SectionCard = ({ icon: Icon, title, children }) => (
  <article className="bg-white rounded-2xl border border-[#E4EEF5] p-6 sm:p-7">
    <div className="w-11 h-11 rounded-xl bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center mb-4">
      <Icon className="w-5 h-5" />
    </div>
    <h2 className="text-xl sm:text-2xl font-[Poppins] font-bold text-[#173A5E] mb-3">
      {title}
    </h2>
    <div className="text-[#3E5871] font-[Inter] text-[15px] sm:text-base leading-relaxed space-y-3">
      {children}
    </div>
  </article>
);

export const WatersportHavenPage = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  const c = pickLang(pageContent.watersport, lang);
  const home = pickLang(pageContent.home, lang);

  return (
    <div data-testid="watersport-haven-page" className="bg-white">
      <Seo title={c.seo.title} description={c.seo.description} lang={lang} />

      {/* Hero */}
      <section
        data-testid="watersport-hero"
        className="relative w-full overflow-hidden"
        style={{ minHeight: "55vh" }}
      >
        <div className="absolute inset-0">
          <img
            src="/media/lemmer-watersport.jpg"
            alt={c.h1}
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(23,58,94,0.35) 0%, rgba(23,58,94,0.35) 40%, rgba(23,58,94,0.85) 100%)",
            }}
          />
        </div>
        <div className="ll-container relative z-10 py-20 sm:py-28">
          <div className="max-w-3xl">
            <span className="inline-block text-[#6BC0E8] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-4 ll-fade-up">
              {c.heroEyebrow}
            </span>
            <h1
              className="text-white text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-4 ll-fade-up"
              style={{ animationDelay: "0.05s" }}
            >
              {c.h1}
            </h1>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          <p className="text-[#3E5871] font-[Inter] text-lg sm:text-xl leading-relaxed">
            {c.intro}
          </p>
        </div>
      </section>

      {/* Kaarten met de vier secties */}
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <SectionCard icon={Anchor} title={c.cards.aanmeren.title}>
              <p>{c.cards.aanmeren.body}</p>
              <p><PlaceholderNote>{c.cards.aanmeren.placeholder}</PlaceholderNote></p>
            </SectionCard>

            <SectionCard icon={Wrench} title={c.cards.voorzieningen.title}>
              <p>{c.cards.voorzieningen.body}</p>
              <p><PlaceholderNote>{c.cards.voorzieningen.placeholder}</PlaceholderNote></p>
            </SectionCard>

            <SectionCard icon={Ship} title={c.cards.botenverhuur.title}>
              <p>{c.cards.botenverhuur.body}</p>
              <p><PlaceholderNote>{c.cards.botenverhuur.placeholder}</PlaceholderNote></p>
            </SectionCard>

            <SectionCard icon={Waves} title={c.cards.dorp.title}>
              <p>{c.cards.dorp.body}</p>
            </SectionCard>
          </div>
        </div>
      </section>

      {/* Sfeer-strook */}
      <section
        className="relative w-full"
        style={{ height: "clamp(220px, 32vw, 380px)" }}
      >
        <img
          src="/media/lemmer-ijsselmeer.jpg"
          alt="IJsselmeer Lemmer"
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </section>

      {/* Ontdek meer */}
      <section className="py-12 sm:py-16">
        <div className="ll-container max-w-3xl">
          <p className="text-sm font-[Poppins] font-semibold uppercase tracking-wider text-[#5A7594] mb-3">
            {t("common.ontdek_meer")}
          </p>
          <div className="flex flex-wrap gap-3">
            <LLink to="/eten-en-drinken" className="ll-btn-primary">
              {home.categories.items["eten-drinken"].title}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink to="/zien-en-doen" className="ll-btn-accent">
              {home.categories.items["zien-doen"].title}
              <ArrowRight className="w-4 h-4" />
            </LLink>
            <LLink to="/praktisch" className="ll-btn-outline">
              {t("nav.praktisch")}
              <ArrowRight className="w-4 h-4" />
            </LLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WatersportHavenPage;
